import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { LogisticsToday, LogisticsPackers } from "@/components/logistics/LogisticsSummary";
import { LogisticsChart } from "@/components/logistics/LogisticsChart";
import { LogisticsOrders } from "@/components/logistics/LogisticsOrders";

const RANGES = [[7, "7 giorni"], [30, "30 giorni"], [90, "90 giorni"]];
const isoDaysAgo = (d) => new Date(Date.now() - d * 864e5).toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

export default function Logistica() {
  const [days, setDays] = useState(30);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["logistics", days],
    queryFn: async () => (await api.get("/logistics", { params: { start: isoDaysAgo(days - 1) } })).data,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.refreshing ? 4000 : false),
  });
  const refresh = useMutation({
    mutationFn: async () => (await api.post("/logistics/refresh")).data,
    onSuccess: () => { toast.success("Aggiornamento da Shopify avviato"); qc.invalidateQueries({ queryKey: ["logistics"] }); },
    onError: () => toast.error("Aggiornamento non riuscito"),
  });
  const busy = refresh.isPending || data?.refreshing;
  return (
    <div data-testid="logistica-page">
      <PageHeader title="Logistica" subtitle="Pacchi preparati ogni giorno e da chi, dai controlli di Scan Picker su Shopify." />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {RANGES.map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`logistics-range-${d}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${days === d ? "border-primary bg-primary text-primary-foreground" : "border-border/60 hover:bg-secondary/50"}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => refresh.mutate()} disabled={busy} data-testid="logistics-refresh-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}{busy ? "Aggiornamento…" : "Aggiorna"}
        </Button>
      </div>
      {isLoading || !data ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="space-y-6">
          <LogisticsToday rows={data.today_by_packer} />
          <Card className="p-6"><LogisticsChart daily={data.daily} packers={data.packers.map((p) => p.name)} /></Card>
          <LogisticsPackers rows={data.packers} total={data.total} days={days} />
          <LogisticsOrders orders={data.orders} total={data.total} />
          {data.refreshed_at && <p className="text-xs text-muted-foreground" data-testid="logistics-refreshed">Ultimo aggiornamento manuale: {new Date(data.refreshed_at).toLocaleString("it-IT")} · si aggiorna anche ogni ora con gli ordini · {num(data.total)} pacchi nel periodo</p>}
        </div>
      )}
    </div>
  );
}
